import * as THREE from 'three';
import { objectsBoundsCenter } from './frame-bounds.js';
import { idsInsideScreenRect } from './selection.js';

const MODES = { move: 'translate', rotate: 'rotate', scale: 'scale' };
const VERBS = { move: 'Move', rotate: 'Rotate', scale: 'Scale' };

// Matrix decomposition and pointer projection introduce float noise. Treat a
// transform as unchanged when each linear component is within one part per
// million (with a 1e-6 absolute floor) and rotation is within 1e-6 radians.
const LINEAR_EPSILON = 1e-6;
const ANGULAR_EPSILON = 1e-6;

function nearlyEqual(a, b) {
  return Math.abs(a - b) <= LINEAR_EPSILON * Math.max(1, Math.abs(a), Math.abs(b));
}

function vectorNearlyEqual(a, b) {
  return nearlyEqual(a.x, b.x) && nearlyEqual(a.y, b.y) && nearlyEqual(a.z, b.z);
}

function transformUnchanged(snapshot) {
  return vectorNearlyEqual(snapshot.object.position, snapshot.position) &&
    vectorNearlyEqual(snapshot.object.scale, snapshot.scale) &&
    snapshot.object.quaternion.angleTo(snapshot.quaternion) <= ANGULAR_EPSILON;
}

function snapshotObject(object) {
  object.updateWorldMatrix(true, false);
  return {
    object,
    parent: object.parent,
    position: object.position.clone(),
    quaternion: object.quaternion.clone(),
    scale: object.scale.clone(),
    worldMatrix: object.matrixWorld.clone(),
  };
}

function restoreObjects(snapshots) {
  for (const snapshot of snapshots) {
    snapshot.object.position.copy(snapshot.position);
    snapshot.object.quaternion.copy(snapshot.quaternion);
    snapshot.object.scale.copy(snapshot.scale);
    snapshot.object.updateMatrix();
  }
  for (const snapshot of snapshots) snapshot.object.updateWorldMatrix(true, false);
}

function commandFor(tool, descriptor) {
  if (tool === 'move') return ['moveObject', { id: descriptor.id, position: descriptor.position }];
  if (tool === 'rotate') return ['rotateObject', { id: descriptor.id, rotation: descriptor.rotationDeg }];
  return ['scaleObject', { id: descriptor.id, scale: descriptor.scale }];
}

/** Direct manipulation tools backed by authoritative bridge transactions. */
export function createTransformTools({ viewport, registry, selection, bridge, localExecution }) {
  const control = viewport.transformControls;
  const canvas = viewport.renderer.domElement;
  const proxy = new THREE.Object3D();
  proxy.name = 'Multi-selection transform pivot';
  let tool = 'select';
  let drag = null;
  let box = null;
  let destroyed = false;
  // True while another module (mesh Edit Mode) owns `control` exclusively.
  // `viewport.transformControls` is one shared TransformControls instance —
  // without this, a vertex-proxy drag driven by mesh-edit-tools.js would
  // also fire this module's own mouseDown/objectChange/mouseUp listeners on
  // the same control, starting a second, conflicting object-level drag on
  // whatever is still object-selected underneath.
  let suspended = false;

  function selectedObjects() {
    return selection.normaliseTopLevel().map((id) => registry.get(id)).filter(Boolean);
  }

  function removeProxy() {
    if (control.object === proxy) control.detach();
    proxy.removeFromParent();
  }

  function refresh() {
    if (destroyed || drag || box || suspended) return;
    selection.prune();
    const objects = selectedObjects();
    if (!MODES[tool] || objects.length === 0) {
      control.detach();
      removeProxy();
      return;
    }

    control.setMode(MODES[tool]);
    if (objects.length === 1) {
      removeProxy();
      control.attach(objects[0]);
      return;
    }

    const center = objectsBoundsCenter(objects);
    if (!proxy.parent) viewport.helpers.add(proxy);
    proxy.position.copy(center);
    viewport.helpers.worldToLocal(proxy.position);
    proxy.quaternion.identity();
    proxy.scale.set(1, 1, 1);
    proxy.updateMatrixWorld(true);
    control.attach(proxy);
  }

  function startDrag() {
    if (suspended) return;
    const objects = selectedObjects();
    if (!MODES[tool] || objects.length === 0) return;
    const snapshots = objects.map(snapshotObject);
    control.object.updateWorldMatrix(true, false);
    drag = {
      tool,
      snapshots,
      multi: objects.length > 1,
      startProxyWorld: control.object.matrixWorld.clone(),
      proxyPosition: proxy.position.clone(),
      proxyQuaternion: proxy.quaternion.clone(),
      proxyScale: proxy.scale.clone(),
      controlsEnabled: viewport.controls.enabled,
    };
    viewport.controls.enabled = false;
  }

  function previewMulti() {
    if (!drag?.multi) return;
    control.object.updateWorldMatrix(true, false);
    const delta = control.object.matrixWorld.clone().multiply(drag.startProxyWorld.clone().invert());
    const parentInverse = new THREE.Matrix4();
    const local = new THREE.Matrix4();
    for (const snapshot of drag.snapshots) {
      snapshot.parent.updateWorldMatrix(true, false);
      parentInverse.copy(snapshot.parent.matrixWorld).invert();
      local.copy(parentInverse).multiply(delta).multiply(snapshot.worldMatrix);
      local.decompose(snapshot.object.position, snapshot.object.quaternion, snapshot.object.scale);
      snapshot.object.updateMatrix();
      snapshot.object.updateWorldMatrix(false, true);
    }
  }

  function restorePreview(state) {
    restoreObjects(state.snapshots);
    if (state.multi) {
      proxy.position.copy(state.proxyPosition);
      proxy.quaternion.copy(state.proxyQuaternion);
      proxy.scale.copy(state.proxyScale);
      proxy.updateMatrixWorld(true);
    }
    viewport.controls.enabled = state.controlsEnabled;
  }

  function multiEntries(state, descriptors) {
    if (state.tool === 'move') return descriptors.map((descriptor) => commandFor('move', descriptor));
    if (state.tool === 'rotate') {
      return descriptors.flatMap((descriptor) => [
        commandFor('move', descriptor),
        commandFor('rotate', descriptor),
      ]);
    }
    return descriptors.flatMap((descriptor) => [
      commandFor('move', descriptor),
      commandFor('rotate', descriptor),
      commandFor('scale', descriptor),
    ]);
  }

  function finishDrag(commit) {
    if (!drag) return;
    const state = drag;
    const changed = commit && state.snapshots.some((snapshot) => !transformUnchanged(snapshot));
    const descriptors = changed ? state.snapshots.map(({ object }) => registry.describe(object)) : [];
    drag = null;
    restorePreview(state);
    refresh();

    if (!changed) return;
    const count = descriptors.length;
    const label = `${VERBS[state.tool]} ${count} ${count === 1 ? 'object' : 'objects'}`;

    // Local Execution / Cloud Sync v1 — a single-object move/rotate/scale
    // commit (the gizmo has already set the object's live transform during
    // the drag; this call only decides whether that result becomes durable
    // via the local-hot path or the gated one) can execute locally when
    // eligible. Without this, restorePreview() above reverts EVERY tool to
    // its pre-drag transform, and only a tool wired to the local-hot path
    // reapplies the final result immediately — any tool left on the gated
    // path instead visibly snaps back to pre-drag until that async round
    // trip returns. Multi-select is deliberately left on the existing gated
    // bridge.executeBatch() path unchanged — this is the single, narrow,
    // already-decided commit point, not a change to how dragging itself
    // behaves.
    if (!state.multi) {
      const [cmd, args] = commandFor(state.tool, descriptors[0]);
      if (localExecution?.eligible(cmd)) {
        try {
          localExecution.execute(cmd, args);
        } catch (error) {
          console.error(`[anvil] ${state.tool} transform failed`, error);
        }
        return;
      }
    }

    const request = state.multi
      ? bridge.executeBatch(multiEntries(state, descriptors), { label })
      : bridge.execute(...commandFor(state.tool, descriptors[0]), { label });
    void request.catch((error) => console.error(`[anvil] ${state.tool} transform failed`, error));
  }

  function forceEndControl() {
    canvas.removeEventListener('pointermove', control._onPointerMove);
    control.dragging = false;
    control.axis = null;
  }

  function cancelBox() {
    if (!box) return;
    const pointerId = box.pointerId;
    box.element.remove();
    viewport.controls.enabled = box.controlsEnabled;
    box = null;
    if (canvas.hasPointerCapture?.(pointerId)) canvas.releasePointerCapture(pointerId);
    refresh();
  }

  function cancel() {
    const hadBox = Boolean(box);
    const hadDrag = Boolean(drag);
    cancelBox();
    if (hadDrag) {
      finishDrag(false);
      forceEndControl();
    }
    return hadBox || hadDrag;
  }

  /**
   * Give exclusive use of `viewport.transformControls` to another module
   * (mesh Edit Mode). Cancels any in-progress drag/box-select and detaches
   * first; `refresh()`/`startDrag()` no-op entirely while suspended, so this
   * module touches neither `control` nor the object selection until resumed.
   */
  function setSuspended(value) {
    const next = Boolean(value);
    if (next === suspended) return;
    if (next) {
      cancel();
      control.detach();
      removeProxy();
      suspended = true;
    } else {
      suspended = false;
      control.enabled = Boolean(MODES[tool]);
      refresh();
    }
  }

  function setTool(nextTool) {
    const normalized = nextTool === 'box-select' ? 'box' : nextTool;
    if (!['select', 'move', 'rotate', 'scale', 'box'].includes(normalized)) {
      throw new Error(`unknown transform tool '${nextTool}'`);
    }
    cancel();
    tool = normalized;
    // While suspended, `control` belongs to mesh Edit Mode — record the
    // requested tool for when setSuspended(false) restores it, but don't
    // touch shared control state (enabled/attach) in the meantime.
    if (!suspended) {
      control.enabled = Boolean(MODES[tool]);
      refresh();
    }
    return tool;
  }

  function startBox(event) {
    if (suspended || tool !== 'box' || event.button !== 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const element = document.createElement('div');
    element.className = 'box-selection';
    canvas.parentElement.append(element);
    box = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      add: event.shiftKey,
      element,
      controlsEnabled: viewport.controls.enabled,
    };
    viewport.controls.enabled = false;
    canvas.setPointerCapture?.(event.pointerId);
    drawBox();
  }

  function drawBox() {
    if (!box) return;
    const canvasRect = canvas.getBoundingClientRect();
    box.element.style.left = `${Math.min(box.startX, box.currentX) - canvasRect.left}px`;
    box.element.style.top = `${Math.min(box.startY, box.currentY) - canvasRect.top}px`;
    box.element.style.width = `${Math.abs(box.currentX - box.startX)}px`;
    box.element.style.height = `${Math.abs(box.currentY - box.startY)}px`;
  }

  function moveBox(event) {
    if (!box || event.pointerId !== box.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    box.currentX = event.clientX;
    box.currentY = event.clientY;
    drawBox();
  }

  function finishBox(event) {
    if (!box || event.pointerId !== box.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const state = box;
    state.currentX = event.clientX;
    state.currentY = event.clientY;
    const ids = idsInsideScreenRect({
      viewport,
      registry,
      rect: { left: state.startX, top: state.startY, right: state.currentX, bottom: state.currentY },
    });
    state.element.remove();
    viewport.controls.enabled = state.controlsEnabled;
    box = null;

    if (!state.add) selection.clear();
    const selected = new Set(selection.ids);
    for (const id of ids) {
      if (selected.has(id)) continue;
      selection.select(id, { toggle: selected.size > 0 });
      selected.add(id);
    }
    refresh();
  }

  function cancelBoxPointer(event) {
    if (box && event.pointerId === box.pointerId) cancelBox();
  }

  function commitDrag() {
    finishDrag(true);
  }

  control.addEventListener('mouseDown', startDrag);
  control.addEventListener('objectChange', previewMulti);
  control.addEventListener('mouseUp', commitDrag);
  canvas.addEventListener('pointerdown', startBox, true);
  canvas.addEventListener('pointermove', moveBox, true);
  canvas.addEventListener('pointerup', finishBox, true);
  canvas.addEventListener('pointercancel', cancelBoxPointer, true);
  setTool('select');

  return {
    get tool() { return tool; },
    get dragging() { return Boolean(drag || box); },
    get suspended() { return suspended; },
    setTool,
    setSuspended,
    cancel,
    refresh,
    destroy() {
      if (destroyed) return;
      cancel();
      destroyed = true;
      control.removeEventListener('mouseDown', startDrag);
      control.removeEventListener('objectChange', previewMulti);
      control.removeEventListener('mouseUp', commitDrag);
      canvas.removeEventListener('pointerdown', startBox, true);
      canvas.removeEventListener('pointermove', moveBox, true);
      canvas.removeEventListener('pointerup', finishBox, true);
      canvas.removeEventListener('pointercancel', cancelBoxPointer, true);
      control.detach();
      control.dispose();
      control.getHelper().removeFromParent();
      removeProxy();
    },
  };
}
